import { Inventory2, MonitorWeight } from "@mui/icons-material";
import { alpha, Box, styled, Typography } from "@mui/material";
import { useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import type { ContextMenuRef } from "../../../components/shared/ContextMenuDropdown";
import {
  previewStyles,
  useDraggable,
} from "../../../components/shared/dragndrop/hooks/useDraggable";
import type { DraggedItem } from "../../../components/shared/dragndrop/types";
import DragIcon from "../../../components/shared/icons/DragIcon";
import { iconFillColors } from "../../../components/shared/icons/Icon.types";
import PalletsIcon from "../../../components/shared/icons/PalletsIcon";
import PiecesIcon from "../../../components/shared/icons/PiecesIcon";
import { ShipmentDragOverlay } from "../../../components/shared/sidebar/ShipmentDragOverlay";
import { TagsGrid } from "../../../components/shared/TagsGrid";
import { dashIfEmpty, formatNumber } from "../../../constants/utils";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import type { OSADShipment } from "../../../types/operations/osad.type";
import { handlingUnitsTransformer } from "../../../utils/handlingUnitsDashTransformer";
import type { SummaryContext } from "../shared/ActionsMenuContext";

const Wrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "flex-start",
  padding: "0.875rem 1rem 0.875rem 0.5rem",
  "&.idle:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&.dragging": {
    opacity: 0.4,
  },
  "&.over": {
    backgroundColor: `${alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)} !important`,
  },
  "&.selected": {
    backgroundColor: `${theme.palette.action.selected} !important`,
  },
  borderRadius: "0.5rem",
}));

const Summary = styled(Box)(() => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
}));

const TitleWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  color: theme.palette.grey[600],
}));

const ActionWrapper = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "1.25rem",
  maxWidth: "1.25rem",
  maxHeight: "1.25rem",
}));

const Title = styled(Typography)(() => ({
  fontSize: "1rem",
  fontWeight: 500,
  lineHeight: "1.25rem",
}));

const Totals = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
}));

const Total = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontSize: "0.875rem",
  lineHeight: "1rem",
  color: theme.palette.grey[600],
}));

const SubText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[500],
  fontSize: "0.875rem",
}));

interface OSADShipmentItemProps {
  shipment: OSADShipment;
  index: number;
  contextRef?: RefObject<ContextMenuRef<SummaryContext>>;
}

export const OSADShipmentItem = ({
  shipment,
  index,
  contextRef,
}: OSADShipmentItemProps) => {
  const isHandlingUnitsEnabled = useIsFeatureFlagEnabled(
    "operations-6016-handling-units-client"
  );
  const itemRef = useRef<HTMLElement | null>(null);
  const dragHandleRef = useRef<HTMLButtonElement | null>(null);

  const [isMouseOver, setIsMouseOver] = useState(false);

  const {
    shipmentId,
    proNumber,
    tags,
    pallets,
    pieces,
    handlingUnits,
    weight,
    weightUnit,
    currentLocation,
    status,
  } = shipment;

  const iconStyles = { fontSize: "1.25rem", color: iconFillColors.darkGray };

  const data: DraggedItem = {
    id: String(shipmentId),
    source: "osad",
    type: "osad",
    index,
    parentId: "-1",
    routeStatus: status,
  };

  const { state, preview, previewElement } = useDraggable({
    id: String(shipmentId),
    index,
    handle: dragHandleRef,
    element: itemRef,
    getInitialData: () => data,
    getData: () => data,
    canDrop: ({ source }) => {
      if (source.element === itemRef.current) return false;
      return source.data.id !== String(shipmentId);
    },
  });

  return (
    <>
      <Wrapper
        ref={itemRef}
        onMouseEnter={() => setIsMouseOver(true)}
        onMouseLeave={() => setIsMouseOver(false)}
        className={`${state.type}`}
        onContextMenu={(event) =>
          contextRef?.current?.openContextMenu(event, {
            id: -1,
            shipmentIds: [shipmentId],
            type: "osad",
          })
        }
      >
        <TitleWrapper>
          <ActionWrapper>
            <DragIcon
              ref={dragHandleRef}
              label={`${proNumber} Drag handle`}
              sx={{
                visibility: `${isMouseOver ? "visible" : "hidden"}`,
                cursor: "grab",
                fontSize: "24px",
              }}
            />
          </ActionWrapper>
        </TitleWrapper>
        <Box sx={{ width: "100%", marginLeft: "0.25rem" }}>
          <Summary paddingBottom="0.25rem">
            <Title>Pro #{proNumber}</Title>
          </Summary>
          <TagsGrid
            tags={tags.map((tag) => ({
              ...tag,
              colorHex: tag.color,
            }))}
          />
          <SubText>{dashIfEmpty(currentLocation.name)}</SubText>
          <Totals>
            <Total>
              <PalletsIcon sx={iconStyles} />
              {formatNumber(pallets)}
            </Total>
            <Total>
              <PiecesIcon sx={iconStyles} />
              {formatNumber(pieces)}
            </Total>
            {isHandlingUnitsEnabled && (
              <Total>
                <Inventory2 sx={iconStyles} />
                {handlingUnitsTransformer(handlingUnits)}
              </Total>
            )}
            <Total>
              <MonitorWeight sx={iconStyles} />
              {`${formatNumber(weight)} ${weightUnit}`}
            </Total>
          </Totals>
        </Box>
      </Wrapper>
      {preview &&
        createPortal(
          <Box ref={previewElement} style={previewStyles(preview)}>
            <ShipmentDragOverlay
              source="sidebar"
              shipment={{
                ...shipment,
                weightUnit: "lbs",
                consignee: { name: shipment.currentLocation.name },
                tags: [...tags].map((tag) => ({ ...tag, colorHex: tag.color })),
              }}
            />
          </Box>,
          document.body
        )}
    </>
  );
};
